import { Machine as GeneratedMachine } from '../../gql/generated';
import { InfoRow, InfoSectionHeader } from '../SidePanel';

// Use the generated Machine type from GraphQL
type Machine = GeneratedMachine;

interface MachineDetailsTabProps {
  machine: Machine;
}

/**
 * EDMS date-times arrive as ISO-8601 UTC strings (the DateTimeISO scalar is
 * untyped), but tolerate a Date in case a type policy ever parses them. Kept
 * verbatim rather than reformatted, so the value matches the raw EDMS response.
 */
function isoString(value: unknown): string {
  if (!value) return '';
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '' : value.toISOString();
  }
  return String(value);
}

/**
 * MachineDetailsTab - Displays machine details in a side panel format
 *
 * Shows Basic Information, Customer Information, Plant Information,
 * and Technical Information in a compact list layout.
 */
export function MachineDetailsTab({ machine }: MachineDetailsTabProps) {
  return (
    <div className='flex flex-col'>
      {/* Basic Information */}
      <section>
        <InfoSectionHeader>Basic information</InfoSectionHeader>
        <div className='flex flex-col gap-2 px-6 py-4'>
          <InfoRow label='Machine ID' value={machine.machineId} />
          <InfoRow
            label='Display Name'
            value={machine.displayName || machine.machineId}
          />
          <InfoRow label='Serial number' value={machine.serial} />
          <InfoRow label='Part Number' value={machine.partNumber} />
          <InfoRow label='SOID' value={machine.soid} />
          <InfoRow label='Family' value={machine.family} />
        </div>
      </section>

      {/* Machine state - the raw EDMS MachineStateDto fields behind the derived
          Status column, labelled with the EDMS field names so a machine can be
          compared directly against GET /api/machines. Always rendered: EDMS
          returns no state at all for a machine that has never reported, and
          that absence is exactly what someone opening this panel needs to see. */}
      <section>
        <InfoSectionHeader>Machine state (EDMS)</InfoSectionHeader>
        <div className='flex flex-col gap-2 px-6 py-4'>
          {machine.state ? (
            <>
              <InfoRow
                label='timeUtc'
                value={isoString(machine.state.timeUtc)}
              />
              <InfoRow
                label='isSynthetic'
                value={
                  machine.state.isSynthetic == null
                    ? ''
                    : String(machine.state.isSynthetic)
                }
              />
              <InfoRow
                label='lastRecordedState'
                value={machine.state.lastRecordedState}
              />
              <InfoRow
                label='lastRecordedStateTimeUtc'
                value={isoString(machine.state.lastRecordedStateTimeUtc)}
              />
            </>
          ) : (
            <InfoRow label='State' value='No state received from EDMS' />
          )}
        </div>
      </section>

      {/* Customer Information - always rendered: missing data shows as
          empty rows rather than a silently dropped section */}
      <section>
        <InfoSectionHeader>Customer Information</InfoSectionHeader>
        <div className='flex flex-col gap-2 px-6 py-4'>
          <InfoRow label='Customer ID' value={machine.customer?.id} />
          <InfoRow label='IFS ID' value={machine.customer?.ifsId} />
          <InfoRow label='Name' value={machine.customer?.name} />
          <InfoRow
            label='Display Name'
            value={machine.customer?.displayName}
          />
          <InfoRow label='Country' value={machine.customer?.country} />
          <InfoRow
            label='Representative'
            value={machine.customer?.representative}
          />
          <InfoRow
            label='Category'
            value={machine.customer?.customerCategory}
          />
        </div>
      </section>

      {/* Plant Information - always rendered, like Customer Information */}
      <section>
        <InfoSectionHeader>Plant Information</InfoSectionHeader>
        <div className='flex flex-col gap-2 px-6 py-4'>
          <InfoRow label='Plant ID' value={machine.plant?.id} />
          <InfoRow label='Plant Name' value={machine.plant?.displayName} />
          <InfoRow label='Country' value={machine.address?.country} />
          <InfoRow label='Country code' value={machine.address?.countryCode} />
        </div>
      </section>

      {/* Edge Device Information - always rendered: a machine without an edge
          device should say so rather than silently drop the section */}
      <section>
        <InfoSectionHeader>Edge Device Information</InfoSectionHeader>
        <div className='flex flex-col gap-2 px-6 py-4'>
          {machine.edgeDevice ? (
            <>
              <InfoRow
                label='Edge Device ID (IoT)'
                value={machine.edgeDevice.deviceId}
              />
              <InfoRow
                label='Edge Device Name'
                value={machine.edgeDevice.displayName}
              />
              <InfoRow
                label='Edge Device DB ID'
                value={machine.edgeDevice.id}
              />
            </>
          ) : (
            <InfoRow label='Edge Device' value='Not provisioned' />
          )}
        </div>
      </section>

      {/* Technical Information */}
      <section>
        <InfoSectionHeader>Technical Information</InfoSectionHeader>
        <div className='flex flex-col gap-2 px-6 py-4'>
          <InfoRow label='Internal ID' value={machine.id} />
          <InfoRow label='Plant Name' value={machine.plant?.displayName} />
          <InfoRow label='Country' value={machine.address?.country} />
        </div>
      </section>
    </div>
  );
}
